/* <predictive-search> - header search combobox (SRCH-01).
   Progressive enhancement over the plain GET form to /search (works without JS).
   250 ms debounce, AbortController for stale requests, per-query cache.
   Results come from the Predictive Search API rendered as the
   predictive-search section (same origin) and go into #predictive-search-results.
   Keys: ArrowDown / ArrowUp move over role=option (aria-activedescendant),
   Enter opens the highlighted option or submits the form, Esc closes the
   results, a second Esc closes the mobile search panel. */
if (!customElements.get('predictive-search')) {
  const DEBOUNCE = 250;
  const MIN_CHARS = 2;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('#predictive-search-results');
      this.container = this.closest('#HeaderSearch');
      this.toggle = document.querySelector('[aria-controls="HeaderSearch"]');
      if (!this.form || !this.input || !this.panel || !window.routes) return;

      this.cache = new Map();
      this.controller = null;
      this.timer = null;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.panel.id);
      this.input.setAttribute('aria-autocomplete', 'list');

      this.onInput = () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), DEBOUNCE);
      };
      this.onKeydown = (event) => this.handleKey(event);
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      };
      this.onPanelPointer = (event) => {
        // Keep focus on the input while clicking a result.
        if (event.target.closest('a')) event.preventDefault();
      };
      this.onPanelClick = (event) => {
        const link = event.target.closest('a');
        if (link) window.location.href = link.href;
      };
      this.onToggle = () => this.toggleMobile();

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      this.panel.addEventListener('mousedown', this.onPanelPointer);
      this.panel.addEventListener('click', this.onPanelClick);
      if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
    }

    disconnectedCallback() {
      if (!this.input) return;
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
      this.panel.removeEventListener('mousedown', this.onPanelPointer);
      this.panel.removeEventListener('click', this.onPanelClick);
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
    }

    get query() {
      return this.input.value.trim();
    }

    async search() {
      const query = this.query;
      if (query.length < MIN_CHARS) {
        this.close();
        return;
      }

      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const url =
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}` +
        '&resources[type]=product&resources[limit]=6' +
        '&resources[options][fields]=title,product_type,variants.title,vendor,tag' +
        '&section_id=predictive-search';

      try {
        this.setAttribute('aria-busy', 'true');
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        const markup = section ? section.innerHTML : '';
        this.cache.set(query, markup);
        // Ignore a late response for a query the user has already changed.
        if (query === this.query) this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    render(markup) {
      if (!markup.trim()) {
        this.close();
        return;
      }
      this.panel.innerHTML = markup;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.input.removeAttribute('aria-activedescendant');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    isOpen() {
      return !this.panel.hidden;
    }

    options() {
      return Array.from(this.panel.querySelectorAll('[role="option"]'));
    }

    selected() {
      return this.panel.querySelector('[role="option"][aria-selected="true"]');
    }

    move(step) {
      const options = this.options();
      if (!options.length) return;
      const current = options.indexOf(this.selected());
      let next = current + step;
      if (current === -1) next = step > 0 ? 0 : options.length - 1;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;

      options.forEach((option) => option.setAttribute('aria-selected', 'false'));
      const option = options[next];
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    handleKey(event) {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (!this.isOpen()) {
            if (this.query.length >= MIN_CHARS) this.search();
            return;
          }
          event.preventDefault();
          this.move(event.key === 'ArrowDown' ? 1 : -1);
          break;
        }
        case 'Enter': {
          const option = this.isOpen() ? this.selected() : null;
          const link = option ? option.querySelector('a') : null;
          if (link) {
            event.preventDefault();
            window.location.href = link.href;
          }
          // No highlighted option: the form submits normally to /search.
          break;
        }
        case 'Escape': {
          if (this.isOpen()) {
            event.preventDefault();
            this.close();
          } else if (this.isMobileOpen()) {
            event.preventDefault();
            this.setMobile(false);
            this.toggle.focus();
          }
          break;
        }
        default:
          break;
      }
    }

    isMobileOpen() {
      return Boolean(this.container && this.toggle && this.container.hasAttribute('data-open'));
    }

    toggleMobile() {
      this.setMobile(!this.isMobileOpen());
    }

    setMobile(open) {
      if (!this.container || !this.toggle) return;
      this.container.toggleAttribute('data-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
      const label = open ? this.toggle.dataset.labelClose : this.toggle.dataset.labelOpen;
      if (label) this.toggle.setAttribute('aria-label', label);
      if (open) {
        this.input.focus();
      } else {
        this.close();
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
